import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Search, Receipt, PieChart, FileText } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { motion } from "framer-motion";
import { PieChart as RechartsPie, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import AccountingDocuments from "./AccountingDocuments";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    minimumFractionDigits: 2
  }).format(amount);
};

const categories = {
  kira: { label: "Kira", color: "#f59e0b" },
  fatura: { label: "Fatura", color: "#3b82f6" },
  malzeme: { label: "Malzeme/Ekipman", color: "#10b981" },
  ulasim: { label: "Ulaşım", color: "#8b5cf6" },
  yemek: { label: "Yemek", color: "#ef4444" },
  personel: { label: "Personel Gideri", color: "#ec4899" },
  vergi: { label: "Vergi", color: "#f97316" },
  sigorta: { label: "Sigorta", color: "#06b6d4" },
  pazarlama: { label: "Pazarlama", color: "#84cc16" },
  yazilim: { label: "Yazılım/Lisans", color: "#6366f1" },
  diger: { label: "Diğer", color: "#71717a" }
};

const paymentMethods = {
  nakit: "Nakit",
  banka: "Banka Havalesi",
  kredi_karti: "Kredi Kartı",
  cek: "Çek"
};

const Expenses = () => {
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    category: "diger",
    subcategory: "",
    amount: "",
    description: "",
    date: "",
    paid: true,
    payment_method: "banka",
    receipt_no: "",
    notes: ""
  });

  const fetchData = useCallback(async () => {
    try {
      let url = `${API}/expenses?month=${selectedMonth}`;
      if (categoryFilter !== "all") {
        url += `&category=${categoryFilter}`;
      }
      
      const [expensesRes, summaryRes] = await Promise.all([
        axios.get(url, { withCredentials: true }),
        axios.get(`${API}/expenses/summary?month=${selectedMonth}`, { withCredentials: true })
      ]);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error("Veriler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, categoryFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        amount: parseFloat(formData.amount)
      };
      
      if (editingExpense) {
        await axios.put(`${API}/expenses/${editingExpense.id}`, data, { withCredentials: true });
        toast.success("Gider güncellendi");
      } else {
        await axios.post(`${API}/expenses`, data, { withCredentials: true });
        toast.success("Gider eklendi");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("İşlem başarısız");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu gider kaydını silmek istediğinizden emin misiniz?")) return;
    try {
      await axios.delete(`${API}/expenses/${id}`, { withCredentials: true });
      toast.success("Gider silindi");
      fetchData();
    } catch (error) {
      toast.error("Silme başarısız");
    }
  };

  const openEditDialog = (expense) => {
    setEditingExpense(expense);
    setFormData({
      category: expense.category,
      subcategory: expense.subcategory || "",
      amount: expense.amount.toString(),
      description: expense.description,
      date: expense.date,
      paid: expense.paid,
      payment_method: expense.payment_method || "banka",
      receipt_no: expense.receipt_no || "",
      notes: expense.notes || ""
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setEditingExpense(null);
    setFormData({
      category: "diger",
      subcategory: "",
      amount: "",
      description: "",
      date: new Date().toISOString().split('T')[0],
      paid: true,
      payment_method: "banka",
      receipt_no: "",
      notes: ""
    });
  };

  const filteredExpenses = expenses.filter(e => 
    e.description?.toLowerCase().includes(search.toLowerCase()) ||
    e.receipt_no?.toLowerCase().includes(search.toLowerCase())
  );

  // Prepare chart data
  const chartData = summary?.by_category ? 
    Object.entries(summary.by_category).map(([key, value]) => ({
      name: categories[key]?.label || key,
      value: value,
      color: categories[key]?.color || "#71717a"
    })) : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="expenses-page">
      {/* Ana Başlık ve Sekmeler */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-2xl font-outfit font-semibold text-zinc-50">Giderler</h1>
      </div>

      <Tabs defaultValue="expenses" className="w-full">
        <TabsList className="bg-zinc-900 border border-zinc-800 p-1">
          <TabsTrigger 
            value="expenses" 
            className="data-[state=active]:bg-amber-400 data-[state=active]:text-zinc-950"
          >
            <Receipt className="w-4 h-4 mr-2" />
            Genel Giderler
          </TabsTrigger>
          <TabsTrigger 
            value="accounting" 
            className="data-[state=active]:bg-rose-500 data-[state=active]:text-white"
          >
            <FileText className="w-4 h-4 mr-2" />
            Muhasebe Evrakları
          </TabsTrigger>
        </TabsList>

        {/* Genel Giderler Sekmesi */}
        <TabsContent value="expenses" className="mt-6 space-y-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <p className="text-zinc-500 text-sm">
                {selectedMonth} Toplam: <span className="text-rose-400">{formatCurrency(summary?.total || 0)}</span>
              </p>
            </div>
            <div className="flex gap-2">
              <Input 
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-40 bg-zinc-900 border-zinc-800"
                data-testid="expense-month-picker"
              />
              <Button 
                onClick={() => { resetForm(); setDialogOpen(true); }}
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="add-expense-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Yeni Gider
              </Button>
            </div>
          </div>

      {/* Summary Cards + Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pie Chart */}
        <div className="lg:col-span-1 bg-zinc-900 border border-zinc-800 p-4 rounded-sm">
          <h3 className="text-sm font-medium text-zinc-400 mb-4 flex items-center gap-2">
            <PieChart className="w-4 h-4" />
            Kategoriye Göre Dağılım
          </h3>
          {chartData.length > 0 ? (
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPie>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip 
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{ 
                      backgroundColor: '#18181b', 
                      border: '1px solid #27272a',
                      borderRadius: '4px'
                    }}
                  />
                </RechartsPie>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 flex items-center justify-center text-zinc-500 text-sm">
              Bu ay gider kaydı yok
            </div>
          )}
        </div>

        {/* Category Breakdown */}
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 p-4 rounded-sm">
          <h3 className="text-sm font-medium text-zinc-400 mb-4">Kategori Detayları</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {Object.entries(summary?.by_category || {}).map(([key, value]) => (
              <div 
                key={key}
                className="p-3 bg-zinc-950 border border-zinc-800 rounded-sm"
              >
                <div className="flex items-center gap-2 mb-1">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: categories[key]?.color || "#71717a" }}
                  />
                  <span className="text-xs text-zinc-400">{categories[key]?.label || key}</span>
                </div>
                <p className="text-lg font-mono text-zinc-50">{formatCurrency(value)}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input 
            placeholder="Gider ara..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800"
            data-testid="expense-search"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48 bg-zinc-900 border-zinc-800" data-testid="expense-category-filter">
            <SelectValue placeholder="Kategori" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {Object.entries(categories).map(([key, { label }]) => (
              <SelectItem key={key} value={key}>{label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <div className="bg-zinc-900 border border-zinc-800 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Kategori</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Açıklama</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tarih</th>
                <th className="text-left px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Ödeme</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">Tutar</th>
                <th className="text-right px-4 py-3 text-xs font-medium text-zinc-500 uppercase tracking-wider">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {filteredExpenses.map((expense, index) => (
                <motion.tr 
                  key={expense.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => openEditDialog(expense)}
                  className="border-b border-zinc-800 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                  data-testid={`expense-row-${expense.id}`}
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: categories[expense.category]?.color || "#71717a" }}
                      />
                      <span className="text-sm text-zinc-50">
                        {categories[expense.category]?.label || expense.category}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <p className="text-sm text-zinc-50">{expense.description}</p>
                    {expense.receipt_no && (
                      <p className="text-xs text-zinc-500">Fiş: {expense.receipt_no}</p>
                    )}
                  </td>
                  <td className="px-4 py-4 text-sm text-zinc-400">
                    {expense.date}
                  </td>
                  <td className="px-4 py-4 text-sm text-zinc-400">
                    {paymentMethods[expense.payment_method] || expense.payment_method}
                  </td>
                  <td className="px-4 py-4 text-right">
                    <span className="text-sm font-mono text-rose-400">-{formatCurrency(expense.amount)}</span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Pencil className="w-3 h-3 text-zinc-500" />
                      <Button 
                        variant="ghost" 
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); handleDelete(expense.id); }}
                        className="hover:bg-rose-400/10 text-zinc-400 hover:text-rose-400"
                        data-testid={`delete-expense-${expense.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </motion.tr>
              ))}
              {filteredExpenses.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-zinc-500">
                    {search || categoryFilter !== "all" ? "Sonuç bulunamadı" : "Bu ay gider kaydı eklenmemiş"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-outfit">
              {editingExpense ? "Gider Düzenle" : "Yeni Gider"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-400">Kategori *</Label>
                <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="expense-category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {Object.entries(categories).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Tutar (₺) *</Label>
                <Input 
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="expense-amount-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Açıklama *</Label>
                <Input 
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="expense-description-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Tarih *</Label>
                <Input 
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="expense-date-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Ödeme Yöntemi</Label>
                <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800" data-testid="expense-payment-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">
                    {Object.entries(paymentMethods).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-zinc-400">Fiş/Fatura No</Label>
                <Input 
                  value={formData.receipt_no}
                  onChange={(e) => setFormData({ ...formData, receipt_no: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="expense-receipt-input"
                />
              </div>
              <div>
                <Label className="text-zinc-400">Alt Kategori</Label>
                <Input 
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  placeholder="Örn: Elektrik, Ofis malzemesi"
                  className="bg-zinc-950 border-zinc-800"
                  data-testid="expense-subcategory-input"
                />
              </div>
              <div className="col-span-2">
                <Label className="text-zinc-400">Notlar</Label>
                <Textarea 
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  className="bg-zinc-950 border-zinc-800"
                  rows={2}
                  data-testid="expense-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
                className="border-zinc-700 hover:bg-zinc-800"
              >
                İptal
              </Button>
              <Button 
                type="submit"
                className="bg-amber-400 hover:bg-amber-300 text-zinc-950"
                data-testid="save-expense-btn"
              >
                {editingExpense ? "Güncelle" : "Kaydet"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
        </TabsContent>

        {/* Muhasebe Evrakları Sekmesi */}
        <TabsContent value="accounting" className="mt-6">
          <AccountingDocuments />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Expenses;
